import { diContainer } from '../DIContainer';
import { AppStorageKey, UserStatusType } from '../types';
import User from '../models/User';
import Role from '../models/Role';
import ApiService from './ApiService';
import Store from '../wrappers/Store';
import { Settings } from '../wrappers';
import { Device } from '../utilities';
import * as bcrypt from 'bcryptjs';
// We only ever COMPARE hashes client-side (PIN / offline-login checks), which
// needs no randomness — the "Module 'crypto' has been externalized" console
// warning at import is bcryptjs probing node crypto and is benign. This
// fallback is purely defensive: if anyone ever adds client-side hashing,
// salts come from Web Crypto instead of bcryptjs's Math.random fallback.
bcrypt.setRandomFallback((len) => Array.from(globalThis.crypto.getRandomValues(new Uint8Array(len))));
const UserService = {
    /**
     * Login the User and store the access token to local storage.
     * If we are offline, check the credentials on client
     * and load user without access token
     *
     * `clockIn: false` logs in without starting a working day (the admin panel).
     */
    login(credentials, { clockIn = true } = {}) {
        if (!Store.get('isOnline')) {
            return this.offlineLogin(credentials.pin, clockIn);
        }
        return new Promise((resolve, reject) => {
            ApiService.post('/user/login', { ...credentials, clock_in: clockIn })
                .then(async (response) => {
                await this.setToken(response.data.access_token);
                await this.setUser(response.data.user);
                const user = new User(response.data.user);
                const isImpersonate = Store.get('impersonate');
                if (!isImpersonate) {
                    user.setSession(response.data.session);
                }
                resolve(user);
            }).catch(error => {
                console.log(error.message);
                reject(error);
            });
        });
    },
    /**
     * Perform offline login
     */
    offlineLogin(pin, clockIn = true) {
        return new Promise(async (resolve, reject) => {
            // A deactivated user cannot log in, as on the online login.
            let loggedInUser = User.all()
                .find(user => user.status !== UserStatusType.disabled && bcrypt.compareSync(pin, user.pin || ''));
            if (loggedInUser) {
                loggedInUser.role = Role.find(loggedInUser.role_id);
                // Load User to the store
                await this.setUser(loggedInUser.$toJson());
                const impersonate = Store.get('impersonate');
                // A session left open since an earlier working day is not continued — the server
                // closes it as stale at the next online login (docs/WorkingHoursSessions.md).
                const session = loggedInUser.getSession();
                if (clockIn && (!session || session.isStale()) && !impersonate) {
                    loggedInUser.clockIn();
                }
                resolve(loggedInUser);
            }
            else {
                reject('invalid_pin');
            }
        });
    },
    /**
     * Logout the current User and remove the token from storage.
     */
    async logout() {
        // Callers open the PIN screen without awaiting the logout, and that screen's
        // requests must not carry the token /user/logout blacklists: drop it from the
        // shared headers before anything is awaited and send it on the logout alone.
        ApiService.removeUserHeader();
        const token = await this.getToken();
        if (Store.get('isOnline') && token) {
            await ApiService.post('/user/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
        }
        Store.set('curOrder', null);
        Store.set('pendingOrderData', null);
        await this.removeToken();
        await this.removeUser();
    },
    /**
     * Clock out current User and remove the token from storage.
     */
    async clockOut() {
        let curUser = User.current();
        if (!curUser)
            return;
        const result = await curUser.clockOut();
        if (!result.success)
            return;
        Store.set('curOrder', null);
        Store.set('pendingOrderData', null);
        await this.removeToken();
        await this.removeUser();
    },
    /**
     * Set User
     */
    async setUser(user) {
        if (user?.role) {
            const isRetail = Store.get('isRetail');
            // Filter user permissions according to the app
            user.role.permissions = user.role?.permissions?.filter(p => {
                if (!isRetail)
                    return p.app !== 'Allegra';
                return !p.app || p.app === 'Allegra';
            }) || [];
        }
        // Save User in the store
        Store.set('user', user);
        Store.set('trainingMode', false);
        // Set User Header
        if (!user) {
            ApiService.removeUserHeader();
            return;
        }
        ApiService.setUserHeader(await this.getToken());
    },
    /**
     * Check if user has a one permission or
     * multiple specified permissions
     *
     * @param permissions
     * @returns {boolean}
     */
    allows(permissions, user) {
        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }
        if (!permissions.length) {
            return true;
        }
        if (!user) {
            user = Store.get('user');
        }
        const userPermissions = user?.role?.permissions || Role.find(user?.role_id)?.permissions;
        if (!userPermissions)
            return false;
        const userPermissionNames = userPermissions.map(p => p.name);
        let allowed = true;
        permissions.forEach(p => {
            if (!userPermissionNames.includes(p)) {
                allowed = false;
            }
        });
        return allowed;
    },
    /**
     * Remove User
     */
    async removeUser() {
        await this.setUser(null);
    },
    /**
     * Get User Token
     */
    async getToken() {
        const appStorage = diContainer.resolve('appStorage');
        return appStorage.local().getItem(AppStorageKey.userToken, null);
    },
    /**
     * Set User Token
     */
    async setToken(accessToken) {
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().setItem(AppStorageKey.userToken, accessToken, null);
    },
    /**
     * Remove User Token
     */
    async removeToken() {
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.local().deleteItem(AppStorageKey.userToken, null);
    },
    canAccessSales() {
        const singleCashRegister = !!Store.get('tenant')?.venue?.settings?.cash_register?.single;
        const isImpersonate = Store.get('impersonate');
        const isWeb = Device.isWeb();
        const isProduction = Settings.env.ENV === 'production';
        return (this.allows('nav_access_tables') || this.allows('nav_access_order') || this.allows('nav_access_sales')) && (singleCashRegister || isImpersonate || !isWeb || !isProduction);
    },
};
export default UserService;
export { UserService };
//# sourceMappingURL=UserService.js.map